import React, { useImperativeHandle, useRef, useState } from 'react';
import { Button, Form, Modal, Spin, Table, message } from 'antd';
import moment from 'moment';
import { ColumnsType } from 'antd/es/table';
import { PaginationProps } from 'antd/es/pagination';
import { connect } from 'react-redux';
import FixStickyTable from '@/components/FixStickyTable';
import { getSmsConfigApply, getSmsConfigLogDetail, getSmsConfigLogPage } from '@/api/sms';
import { DATE_TIME_FORMAT, SmsSystemSourceMap } from '@/config';
import { renderIntlLabel } from '@/utils';
import CustomDrawer, { IdrawerRef } from '../CustomDrawer';
import styles from './index.module.scss';

const SmsOpenDrawer = ({ currentShop, myRef }) => {
  const [smsConfigInfo, setSmsConfigInfo] = useState<any>({});
  const [configLoading, setConfigLoading] = useState(false);

  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    pageNum: 1,
    pageSize: 50,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const [open, setOpen] = useState(false);

  const drawerRef = useRef<IdrawerRef>();

  const initConfigLog = async (values?) => {
    const params = {
      pageNum: 1,
      pageSize: 50,
      ...values,
    };
    try {
      setLoading(true);
      const res = await getSmsConfigLogPage(params);
      if (res.code === 0) {
        const { total, list } = res.data;
        setDataSource(list);
        setPagination({ pageNum: params.pageNum, pageSize: params.pageSize, total });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const getSmsConfig = async () => {
    try {
      setConfigLoading(true);
      const res = await getSmsConfigLogDetail();
      if (res.code === 0) {
        setSmsConfigInfo(res.data);
      }
      setConfigLoading(false);
    } catch (error) {
      setConfigLoading(false);
    }
  };

  const handleOpenDrawer = () => {
    drawerRef.current.openDrawer();
    initConfigLog();
    getSmsConfig();
  };

  const handleOk = async () => {
    try {
      setApplyLoading(true);
      const res = await getSmsConfigApply({
        storeCode: currentShop?.commonCode,
        storeName: currentShop?.shopName,
      });
      if (res.code === 0) {
        message.success(renderIntlLabel('app_send_successfully'));

        setOpen(false);
        initConfigLog();
        getSmsConfig();
      }
      setApplyLoading(false);
    } catch (error) {
      setApplyLoading(false);
    }
  };

  const handleTableChange = (pagi: PaginationProps) => {
    const { current, pageSize } = pagi;
    initConfigLog({ pageNum: current, pageSize });
  };

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const columns: ColumnsType<any> = [
    { title: renderIntlLabel('expenses_operate_content'), dataIndex: 'remark' },
    {
      title: renderIntlLabel('order_selfService_detail_source'),
      dataIndex: 'system',
      render: (val) => SmsSystemSourceMap[val],
    },
    {
      title: renderIntlLabel('roomstate_take_care_record_column_crate_time'),
      dataIndex: 'createTime',
      render: (val) => (val ? moment(val).format(DATE_TIME_FORMAT) : '-'),
    },
    {
      title: renderIntlLabel('guest_coupon_label_operator'),
      dataIndex: 'createBy',
      render: (val, record) =>
        val || record.createName
          ? `${val || ''}${record.createName ? `【${record.createName}】` : ''}`
          : '-',
    },
  ];

  return (
    <>
      <CustomDrawer
        className={styles.smsContainer}
        width="50%"
        title={renderIntlLabel('guest_title_sms_send_service')}
        ref={drawerRef}
        footer={
          <Button onClick={() => drawerRef.current.closeDrawer()}>
            {renderIntlLabel('order_action_close')}
          </Button>
        }
      >
        <div className="groupLabel" style={{ marginBottom: '16px' }}>
          {renderIntlLabel('guest_title_sms_service_explain')}
        </div>
        <div>
          <div className={styles.descStart}>{renderIntlLabel('app_sms_tips_1')}</div>
          <div>1、{renderIntlLabel('app_sms_tips_2')}</div>
          <div>2、{renderIntlLabel('app_sms_tips_3')}</div>
          <div>3、{renderIntlLabel('app_sms_tips_4')}</div>
          <div>4、{renderIntlLabel('app_sms_tips_5')}</div>
          <div>5、{renderIntlLabel('app_sms_tips_6')}</div>
          <div>6、{renderIntlLabel('app_sms_tips_7')}</div>
          <div className={styles.descEnd}>{renderIntlLabel('app_sms_tips_8')}</div>
        </div>

        <Spin spinning={configLoading}>
          <div className="groupLabel" style={{ marginTop: '32px', marginBottom: '16px' }}>
            {renderIntlLabel('app_sms_open_service_apply')}
          </div>
          <Form.Item label={renderIntlLabel('app_sms_send_service')}>
            {smsConfigInfo?.open === 1
              ? renderIntlLabel('app_sms_had_open')
              : renderIntlLabel('app_sms_no_open')}
            <span className={styles.refreshBtn} onClick={getSmsConfig}>
              {renderIntlLabel('app_sms_refresh_status')}
            </span>
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={() => setOpen(true)}>
              {renderIntlLabel('app_sms_apply_to_open_service_immediately')}
            </Button>
          </Form.Item>
        </Spin>

        <FixStickyTable>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={{
              current: pagination?.pageNum,
              pageSize: pagination.pageSize,
              total: pagination.total,
            }}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
          />
        </FixStickyTable>

        <Modal
          title={renderIntlLabel('app_sms_apply_to_open_service')}
          open={open}
          onOk={handleOk}
          onCancel={() => setOpen(false)}
          okButtonProps={{ loading: applyLoading }}
        >
          {renderIntlLabel('app_sms_tips_click_ok_button')}
        </Modal>
      </CustomDrawer>
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentShop } = state.user;
  return { currentShop };
};

const ConnectComponent: React.FC<any> = connect(mapStateToProps, {})(React.memo(SmsOpenDrawer));

export default React.forwardRef<any, any>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
